import { Position } from '../../../../models/api/vehicle-position.model';
import { AppSelector } from '../../../../core/state/app-selector';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { ReportsService } from '../../../core/reports.service';
import { of, Subscription } from 'rxjs';
import { tap, catchError, take, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../../../core/permissions.service';
import { ReportOptionsService } from '../report-options.service';

@Component({
  selector: 'app-set-group-selector',
  templateUrl: './set-group-selector.component.html',
  styleUrls: ['./set-group-selector.component.scss'],
})
export class SetGroupSelectorComponent implements OnInit, OnDestroy {
  set: any;
  setOptions = [];
  selectedSet: any = {};
  setIsLoading = true;
  groupSubscription: Subscription;

  @Input() reportId: number;
  @Input() type: string;
  @Input() label = this.translateService.instant('reports.vehicle');
  @Input() allSetText = this.translateService.instant(
    'reports.all-sets'
  );
  @Input() placeholder = this.translateService.instant(
    'reports.select-set'
  );
  @Input() selectedSetId: number;
  @Input() allSetsOption: boolean;
  @Input() forkliftsSelector: boolean;
  @Input() disableFilterByType: boolean = true;
  @Input() dropdownClass: string = 'dropdown--gray';
  @Input() dropdownIconClass: string = 'fa fa-chevron-down';
  @Output() selectedSetIdChange = new EventEmitter<number>();

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    private reportOptionsService: ReportOptionsService,
    private appSelector: AppSelector
  ) {}

  ngOnInit() {
    this.loadVehicles();
  }

  private onSetGroupChange(): (value: any) => void {
    return (res) => {
      this.prepareGroupFilter(res);
      this.filterAndSelectSet();
    };
  }

  private onFilterTypeChange(): (value: any) => void {
    return (res) => {
      this.prepareTypeFilter(res);
      this.filterAndSelectSet();
    };
  }

  private filterAndSelectSet() {
    this.setIsLoading = true;
    this.setOptions = this.prepareSetOptions();
    this.selectVehicle();
    this.reportOptionsService.vehicleName = this.selectedSet.label;
    this.setIsLoading = false;
  }

  private prepareGroupFilter(res: any) {
    if (typeof res === 'object') {
      this.reportsService.filterByGroupIsEnable = true;
      this.reportsService.filterGroupIdArray = res;
    } else {
      this.reportsService.filterByGroupIsEnable = false;
    }
  }

  private prepareTypeFilter(res: any) {
    if (res > 0) {
      this.reportsService.filterByTypeIsEnable = true;
      this.reportsService.filterTypeId = res;
    } else {
      this.reportsService.filterByTypeIsEnable = false;
    }
  }

  prepareSetOptions(): any[] {
    if (this.set) {
      const filteredVehicles = this.filterVehiclesList();
      if (this.allSetsOption && filteredVehicles.length > 1) {
        return [...this.getAllSetOption(), ...filteredVehicles];
      }
      return filteredVehicles;
    } else {
      return [];
    }
  }

  getAllSetOption(): any {
    return [
      {
        label: this.forkliftsSelector
          ? this.translateService.instant('reports.all-forklifts')
          : this.allSetText,
        value: 0,
      },
    ];
  }

  private filterVehiclesList(): any[] {
    return this.set
      .filter(
        (vehicle) =>
          !this.reportsService.filterByGroupIsEnable ||
          this.filterByGroup(vehicle)
      )
      .filter(
        (vehicle) =>
          this.disableFilterByType ||
          !this.reportsService.filterByTypeIsEnable ||
          this.filterByType(vehicle)
      )
      .map((vehicle) => {
        return { label: vehicle.description, value: vehicle.id };
      });
  }

  filterByType(vehicle: any): boolean {
    return vehicle.typeId === this.reportsService.filterTypeId;
  }
  filterByGroup(vehicle: any): boolean {
    return this.reportsService.filterGroupIdArray.indexOf(vehicle.id) !== -1;
  }

  selectVehicle() {
    if (this.setOptions.length > 0) {
      this.appSelector
        .getSelectedVehicle()
        .pipe(
          take(1),
          map((position: Position) => {
            if (position) {
              return position.vehicle;
            }
          })
        )
        .subscribe((globalSelectedVehicleId) => {
          if (this.selectedSetId > 1) {
            this.selectVehicleById(this.selectedSetId);
          } else {
            if (globalSelectedVehicleId) {
              this.selectVehicleById(globalSelectedVehicleId);
            } else {
              this.selectedSet = this.setOptions[0];
            }
          }
          this.selectedSetId = this.selectedSet.value;
          this.selectedSetIdChange.emit(this.selectedSet.value);
        });
    } else {
      this.selectedSetId = -1;
      this.selectedSetIdChange.emit(-1);
    }
  }

  private selectVehicleById(vehicleId) {
    this.selectedSet = this.setOptions.find(
      (vehiclesOption) => vehiclesOption.value.toString() === vehicleId.toString()
    );
    if (!this.selectedSet) {
      this.selectedSet = this.setOptions[0];
    }
  }

  ngOnDestroy() {
    if (this.groupSubscription) {
      this.groupSubscription.unsubscribe();
    }
  }

  loadVehicles() {
    this.appSelector
      .getSortedPositions()
      .pipe(take(1))
      .subscribe((positions) => {
        this.reportsService
          .getVehicles(this.reportId)
          .pipe(
            tap((data: any[]) => {
              this.set = positions
                .map((position) => {
                  return {
                    ...data.find((el) => el.id.toString() === position.vehicle.toString()),
                  };
                })
                .filter((position) => position.typeId !== undefined);
              this.filterAndSelectSet();
              this.groupSubscription = this.reportsService.filterByVehicleGroupId.subscribe(
                this.onSetGroupChange()
              );

              this.reportsService.filterByVehicleTypeId.subscribe(
                this.onFilterTypeChange()
              );
              this.onSelectedVehicleIdChange();
            }),
            catchError((error, caught) => {
              this.setOptions = [];
              this.set = [];
              this.setIsLoading = false;
              this.appSelector.getSelectedVehicle().subscribe(vehicle => {
                if (vehicle) {
                  this.setOptions.push({label: vehicle.name, value: vehicle.vehicle});
                  this.selectedSet = this.setOptions[0];
                  this.selectedSetId = this.selectedSet.value;
                  this.selectedSetIdChange.emit(this.selectedSetId);
                } else {
                  this.selectedSetIdChange.emit(-1);
                }
              });
              return of();
            })
          )
          .subscribe();
      });
  }

  onSelectedVehicleIdChange() {
    if (this.selectedSet) {
      this.selectedSetIdChange.emit(this.selectedSet.value);
      this.reportOptionsService.vehicleName = this.selectedSet.label;
      // this.permissionsService
      //   .getArchivePermissions(this.reportId, this.selectedSet.value, -1)
      //   .subscribe((res) => {
      //     this.permissionsService.setArchivePermission.next(res);
      //   });
    } else {
      this.selectedSetIdChange.emit(-1);
      // this.permissionsService
      //   .getArchivePermissions(this.reportId, 0, -1)
      //   .subscribe((res) => {
      //     this.permissionsService.setArchivePermission.next(res);
      //   });
    }
  }
}
